import {
  Dictionary,
  Literal,
  Null,
  Optional,
  Array as RArray,
  Boolean as RBoolean,
  Number as RNumber,
  Record as RRecord,
  Record,
  Static,
  String,
  Union,
  Unknown,
} from "runtypes";

import { DataConnectionTypeLiteral } from "../../dataConnectionTypes.js";
import { DisplayTableOutput } from "../../display-table/index.js";
import { CellTypeRuntype } from "../../esDocTypeEnum.js";
import {
  AgentChatThreadId,
  CellId,
  DataConnectionId,
  DataSourceTableId,
  FileId,
  HexId,
  OrgGuideFileId,
  OrgGuideFileVersionId,
  SemanticDatasetId,
  SemanticProjectFileId,
  SemanticProjectFilePath,
  SemanticProjectId,
  SemanticViewId,
  StaticCellId,
} from "../../idTypeBrands.js";
import {
  MagicSupportedImageMimeTypes,
  MentionedDataframeName,
} from "../../MagicTypes.js";
import { getNormalEnum } from "../../runtypeEnums.js";

import { MessageAskUserBlock } from "./askUser.js";
import { AgentChatThreadType } from "./threads";
import { ToolIconLiteral } from "./tools/icons.js";
import {
  DataPreviewColumn,
  JoinEdge,
  KnowledgeSearchDisplayAtom,
  LinkItem,
  LocalJoinGraph,
  RelatedProject,
  SemanticColumnItem,
  SemanticProjectItem,
  SemanticResourceItem,
  TableItem,
} from "./tools/sharedTypes.js";

export const MessageRoleLiteral = Union(Literal("user"), Literal("agent"));
export type MessageRole = Static<typeof MessageRoleLiteral>;
export const MessageRole = getNormalEnum(MessageRoleLiteral);
const AssistantPhaseLiteral = Union(
  Literal("commentary"),
  Literal("final_answer"),
);
export const ConversationMessageRoleLiteral = Union(
  Literal("user"),
  Literal("agent"),
  Literal("assistant"),
  Literal("system"),
);
export type ConversationMessageRole = Static<
  typeof ConversationMessageRoleLiteral
>;

export const MessageThinkingBlock = RRecord({
  type: Literal("thinking"),
  text: String,
  providerMetadata: Optional(
    RRecord({
      signature: Optional(String),
      /** @deprecated Use toolCallIds for new messages (EDT-1472) */
      toolCallId: Optional(String),
      toolCallIds: Optional(RArray(String)),
      phase: Optional(AssistantPhaseLiteral),
    }),
  ),
});
export type MessageThinkingBlock = Static<typeof MessageThinkingBlock>;

/**
 * A block that represents a text/markdown message.
 */
export const MessageTextBlock = RRecord({
  type: Literal("text"),
  text: String,
  providerMetadata: Optional(
    RRecord({
      /** @deprecated Use toolCallIds for new messages (EDT-1472) */
      toolCallId: Optional(String),
      toolCallIds: Optional(RArray(String)),
      phase: Optional(AssistantPhaseLiteral),
    }),
  ),
});
export type MessageTextBlock = Static<typeof MessageTextBlock>;

/**
 * Transient progress block shown while provider-native server compaction runs.
 */
export const MessageCompressionTriggeredBlock = RRecord({
  type: Literal("compression_triggered"),
});
export type MessageCompressionTriggeredBlock = Static<
  typeof MessageCompressionTriggeredBlock
>;

export const LlmProviderLiteral = Union(
  Literal("anthropic"),
  Literal("openai"),
);
export type LlmProvider = Static<typeof LlmProviderLiteral>;

/**
 * Identifies the mechanism that produced a server-side compaction payload.
 * "anthropic"/"openai" are provider-native compaction; "manual" is Hex's own
 * server-side summarization. This is intentionally distinct from
 * {@link LlmProviderLiteral} (which names real LLM API providers) because
 * "manual" is not an LLM provider.
 */
export const CompactionProviderLiteral = Union(
  Literal("anthropic"),
  Literal("openai"),
  Literal("manual"),
);
export type CompactionProvider = Static<typeof CompactionProviderLiteral>;

/**
 * Server-side compaction payload to carry forward in history.
 * Anthropic/OpenAI payloads are provider-native and opaque; manual payloads are
 * plain-text summaries replayed via the chat-completions conformer.
 */
export const MessageServerCompactionBlock = RRecord({
  type: Literal("server_compaction"),
  // Identifies which compaction mechanism produced this block.
  provider: CompactionProviderLiteral,
  content: String,
});
export type MessageServerCompactionBlock = Static<
  typeof MessageServerCompactionBlock
>;

export const MessageToolCallBlock = RRecord({
  type: Literal("tool_call"),
  toolName: String,
  arguments: String,
  label: Optional(String),
  icon: Optional(ToolIconLiteral),
  providerMetadata: Optional(
    RRecord({
      toolCallId: Optional(String),
      provider: Optional(LlmProviderLiteral),
      phase: Optional(AssistantPhaseLiteral),
      openaiFunctionCallNamespace: Optional(String),
    }),
  ),
});
export type MessageToolCallBlock = Static<typeof MessageToolCallBlock>;

export const ToolResultTypes = Union(
  Literal("string"),
  Literal("activity"),
  Literal("table_list"),
  Literal("semantic-column-list"),
  Literal("knowledge-search-list"),
  Literal("semantic-project-list"),
  Literal("semantic-resource-list"),
  Literal("display_table"),
  Literal("error"),
  Literal("image"),
  Literal("related-projects"),
  Literal("semantic-model"),
  Literal("semantic-project-file"),
  Literal("look-up-project"),
  Literal("empty"),
  Literal("sql-and-preview-table"),
  Literal("list-of-links"),
  Literal("cancelled"),
  Literal("cell-list"),
  Literal("image-file"),
  Literal("text-file"),
  Literal("org-guide-file-list"),
  Literal("read-guide"),
  Literal("hidden"),
  Literal("mcp-tool-call"),
  Literal("data_connection_list"),
  Literal("suggest-review"),
  Literal("user-question"),
  Literal("data-app-file-paths"),
  Literal("data-app-grep-hits"),
  Literal("file-diff"),
);
export type ToolResultType = Static<typeof ToolResultTypes>;

/**
 * Base type for all tool results with optional label field
 */
const BaseToolResult = RRecord({
  type: ToolResultTypes,
  label: String,
  icon: ToolIconLiteral,
  /**
   * Optional muted text shown right-aligned in the tool result header,
   * useful for counts/badges (e.g. "12 files", "no matches")
   */
  secondaryLabel: Optional(String),
});

const EmptyResult = BaseToolResult.extend({
  type: Literal("empty"),
  content: Null,
});
export type EmptyResult = Static<typeof EmptyResult>;

export const ToolResultSourceLiteral = Union(
  Literal("browserbase"),
  Literal("screenshot_service"),
  /** @deprecated Client iframe capture removed; retained for persisted tool results. */
  Literal("client"),
);
export type ToolResultSource = Static<typeof ToolResultSourceLiteral>;

const StringResult = BaseToolResult.extend({
  type: Literal("string"),
  content: String,
  source: Optional(ToolResultSourceLiteral),
});
export type StringResult = Static<typeof StringResult>;

/** Text with a dedicated label and Markdown body for the refreshed activity timeline. */
const ActivityResult = BaseToolResult.extend({
  type: Literal("activity"),
  content: String,
  activity: RRecord({ label: String, content: String }),
});
export type ActivityResult = Static<typeof ActivityResult>;

const TableListResult = BaseToolResult.extend({
  type: Literal("table_list"),
  tables: RArray(TableItem),
});
export type TableListResult = Static<typeof TableListResult>;

export const ErrorResult = BaseToolResult.extend({
  type: Literal("error"),
  message: Union(String, Null),
  trace: Union(String, Null),
});
export type ErrorResult = Static<typeof ErrorResult>;

export const CancelledResult = BaseToolResult.extend({
  type: Literal("cancelled"),
});
export type CancelledResult = Static<typeof CancelledResult>;

export const DisplayTableResult = BaseToolResult.extend({
  type: Literal("display_table"),
  rows: DisplayTableOutput,
});
export type DisplayTableResult = Static<typeof DisplayTableResult>;

export const ImageResult = BaseToolResult.extend({
  type: Literal("image"),
  mimeType: MagicSupportedImageMimeTypes,
  dataUri: String,
  source: Optional(ToolResultSourceLiteral),
});
export type ImageResult = Static<typeof ImageResult>;

const RelatedProjectsResult = BaseToolResult.extend({
  type: Literal("related-projects"),
  content: RRecord({
    hexes: RArray(RelatedProject),
  }),
});
export type RelatedProjectsResult = Static<typeof RelatedProjectsResult>;

const LinkListResult = BaseToolResult.extend({
  type: Literal("list-of-links"),
  content: RRecord({
    links: RArray(LinkItem),
  }),
});
export type LinkListResult = Static<typeof LinkListResult>;

const SemanticColumnListResult = BaseToolResult.extend({
  type: Literal("semantic-column-list"),
  content: RArray(SemanticColumnItem),
  /** Human-readable title of the inspected semantic dataset or view. */
  semanticResourceTitle: Optional(String.Or(Null)),
  /** Optional markdown text to display (e.g., data preview) - kept for backwards compatibility */
  additionalMarkdown: Optional(String),
  /** Structured local join graph for Mode 3 visualization */
  localJoinGraph: Optional(LocalJoinGraph),
  /** Structured data preview rows for rich table rendering */
  dataPreviewRows: Optional(RArray(DataPreviewColumn)),
  /** Total row count returned by the preview query */
  totalRowCount: Optional(RNumber),
});
export type SemanticColumnListResult = Static<typeof SemanticColumnListResult>;

const KnowledgeSearchListResult = BaseToolResult.extend({
  type: Literal("knowledge-search-list"),
  content: RRecord({
    markdown: String,
    queries: RArray(String),
    resultCount: RNumber,
    results: Optional(RArray(KnowledgeSearchDisplayAtom)),
  }),
});
export type KnowledgeSearchListResult = Static<
  typeof KnowledgeSearchListResult
>;

const SemanticProjectListResult = BaseToolResult.extend({
  type: Literal("semantic-project-list"),
  content: RArray(SemanticProjectItem),
});
export type SemanticProjectListResult = Static<
  typeof SemanticProjectListResult
>;

const SemanticResourceListResult = BaseToolResult.extend({
  type: Literal("semantic-resource-list"),
  content: RArray(SemanticResourceItem),
  /** The project these resources belong to */
  semanticProjectId: SemanticProjectId,
  semanticProjectName: String,
  /** Optional markdown text to display (e.g., join graph information) */
  additionalMarkdown: Optional(String),
  /** Structured join edges for full project join graph visualization (Mode 2) */
  joinEdges: Optional(RArray(JoinEdge)),
});
export type SemanticResourceListResult = Static<
  typeof SemanticResourceListResult
>;

/** @deprecated - No longer used, replaced by `SemanticProjectFileResult`. */
const SemanticDatasetResult = BaseToolResult.extend({
  type: Literal("semantic-model"),
  content: RRecord({
    semanticDatasetId: SemanticDatasetId,
    semanticDatasetName: String,
    originalYml: Optional(String),
    modifiedYml: Optional(String),
  }),
});
export type SemanticDatasetResult = Static<typeof SemanticDatasetResult>;

const SemanticProjectFileResult = BaseToolResult.extend({
  type: Literal("semantic-project-file"),
  content: Record({
    semanticProjectFileId: SemanticProjectFileId,
    filePath: SemanticProjectFilePath,
    contentsText: Optional(
      Record({
        original: Optional(String),
        modified: Optional(String),
      }),
    ),
  }),
});
export type SemanticProjectFileResult = Static<
  typeof SemanticProjectFileResult
>;

const LookUpProjectResult = BaseToolResult.extend({
  type: Literal("look-up-project"),
  hexId: Optional(HexId),
  content: Record({
    project: Record({
      title: String,
      description: Optional(String.Or(Null)),
    }),
    cells: RArray(
      Record({
        staticCellId: StaticCellId,
        /** @deprecated Use staticCellId instead. */
        cellId: Optional(CellId.Or(Null)),
        label: String,
        cellType: CellTypeRuntype,
        contents: String,
      }),
    ),
  }),
});
export type LookUpProjectResult = Static<typeof LookUpProjectResult>;

const McpToolCallResult = BaseToolResult.extend({
  type: Literal("mcp-tool-call"),
  request: Unknown,
  toolName: String,
  response: Unknown,
});

export type McpToolCallResult = Static<typeof McpToolCallResult>;

const SQLAndPreviewTableResult = BaseToolResult.extend({
  type: Literal("sql-and-preview-table"),
  sqlContent: String,
  totalRowCount: RNumber,
  rows: RArray(DataPreviewColumn),
  totalRowCountOfPreview: Optional(RBoolean),
  dataConnectionName: Optional(String),
  dataConnectionType: Optional(DataConnectionTypeLiteral),
});
export type SQLAndPreviewTableResult = Static<typeof SQLAndPreviewTableResult>;

/**
 * Displays a list of clickable cells.
 */
export const CellListResult = BaseToolResult.extend({
  type: Literal("cell-list"),
  content: RRecord({
    staticCellIds: Optional(RArray(StaticCellId)),
    /** @deprecated Use staticCellIds instead. */
    cellIds: Optional(RArray(CellId)),
  }),
});
export type CellListResult = Static<typeof CellListResult>;

const ImageFileResult = BaseToolResult.extend({
  type: Literal("image-file"),
  content: Record({
    fileId: FileId,
    fileName: String,
  }),
});
export type ImageFileResult = Static<typeof ImageFileResult>;

const TextFileResult = BaseToolResult.extend({
  type: Literal("text-file"),
  content: Record({
    fileId: FileId,
    fileName: String,
  }),
});
export type TextFileResult = Static<typeof TextFileResult>;

const OrgGuideFileListResult = BaseToolResult.extend({
  type: Literal("org-guide-file-list"),
  orgGuideFiles: RArray(
    Record({
      filePath: String,
      /** The guide's frontmatter `name`, when it declares one. */
      frontmatterName: Optional(String),
      /** The guide's frontmatter `description`, when it declares one. */
      frontmatterDescription: Optional(String),
      sectionPath: Optional(String),
      matchedSectionPaths: Optional(RArray(String)),
      orgGuideFileId: OrgGuideFileId,
      // only link the content by `orgGuideFileVersionId` since we don't want
      // to store big blobs of text extra times in the db, and versions are immutable
      orgGuideFileVersionId: OrgGuideFileVersionId,
    }),
  ),
});
export type OrgGuideFileListResult = Static<typeof OrgGuideFileListResult>;

const ReadGuideResult = BaseToolResult.extend({
  type: Literal("read-guide"),
  content: String,
  orgGuideFileId: Optional(OrgGuideFileId),
  orgGuideFileVersionId: Optional(OrgGuideFileVersionId),
});
export type ReadGuideResult = Static<typeof ReadGuideResult>;

const DataConnectionListResult = BaseToolResult.extend({
  type: Literal("data_connection_list"),
  content: RArray(DataConnectionId),
});
export type DataConnectionListResult = Static<typeof DataConnectionListResult>;

const HiddenResult = RRecord({
  type: Literal("hidden"),
});
export type HiddenResult = Static<typeof HiddenResult>;

/** Use this for tools that don't need to display anything in the UI. */
export const HIDDEN_RESULT: HiddenResult = { type: "hidden" };

export const SuggestReviewResult = BaseToolResult.extend({
  type: Literal("suggest-review"),
});
export type SuggestReviewResult = Static<typeof SuggestReviewResult>;

export const MultiChoiceQuestionResult = BaseToolResult.extend({
  type: Literal("user-question"),
  content: RRecord({
    question: String,
    answers: RArray(String),
  }),
});
export type MultiChoiceQuestionResult = Static<
  typeof MultiChoiceQuestionResult
>;

/**
 * Tool result that resolved to a list of data app file paths. Used by data
 * app file tools (write/edit/read/move/copy and their multi-file variants)
 * to render a clickable header / row per path that opens the file in the
 * App Builder source panel. Carries paths directly so the client never has
 * to parse them out of labels or arguments.
 */
export const DataAppFilePathsResult = BaseToolResult.extend({
  type: Literal("data-app-file-paths"),
  /** Paths the row should treat as click targets, in display order. */
  paths: RArray(String),
});
export type DataAppFilePathsResult = Static<typeof DataAppFilePathsResult>;

/**
 * Tool result for `GrepDataAppFiles`. Carries the search pattern plus a
 * structured per-file hit list so the client can render search-term tags in
 * the title and a clickable row per matched file (with match count) without
 * having to re-parse a stringified body.
 */
export const DataAppGrepHitsResult = BaseToolResult.extend({
  type: Literal("data-app-grep-hits"),
  /** Original regex pattern the agent searched for (alternation-split client-side into tags). */
  pattern: String,
  hits: RArray(
    RRecord({
      path: String,
      /** Match count for the file. `0` is allowed for `filesOnly` mode where we don't enumerate matches. */
      matchCount: RNumber,
    }),
  ),
});
export type DataAppGrepHitsResult = Static<typeof DataAppGrepHitsResult>;

export const AiderDiffDisplayLineKind = Union(
  Literal("added"),
  Literal("context"),
  Literal("removed"),
);
export type AiderDiffDisplayLineKind = Static<typeof AiderDiffDisplayLineKind>;

export const AiderDiffDisplayLine = RRecord({
  kind: AiderDiffDisplayLineKind,
  text: String,
  newLineNumber: Optional(RNumber),
  oldLineNumber: Optional(RNumber),
});
export type AiderDiffDisplayLine = Static<typeof AiderDiffDisplayLine>;

export const AiderDiffHunk = RRecord({
  displayLines: RArray(AiderDiffDisplayLine),
});
export type AiderDiffHunk = Static<typeof AiderDiffHunk>;

/**
 * Structured unified diff lines for agent file-edit tool results.
 */
export const FileDiffToolResult = BaseToolResult.extend({
  type: Literal("file-diff"),
  files: RArray(
    RRecord({
      path: String,
      hunks: RArray(AiderDiffHunk),
    }),
  ),
});
export type FileDiffToolResult = Static<typeof FileDiffToolResult>;

export const ToolResult = Union(
  EmptyResult,
  StringResult,
  ActivityResult,
  TableListResult,
  DisplayTableResult,
  ErrorResult,
  CancelledResult,
  ImageResult,
  RelatedProjectsResult,
  LinkListResult,
  SemanticColumnListResult,
  KnowledgeSearchListResult,
  SemanticProjectListResult,
  SemanticResourceListResult,
  SemanticDatasetResult,
  SemanticProjectFileResult,
  LookUpProjectResult,
  SQLAndPreviewTableResult,
  McpToolCallResult,
  CellListResult,
  ImageFileResult,
  TextFileResult,
  OrgGuideFileListResult,
  DataConnectionListResult,
  HiddenResult,
  ReadGuideResult,
  SuggestReviewResult,
  MultiChoiceQuestionResult,
  DataAppFilePathsResult,
  DataAppGrepHitsResult,
  FileDiffToolResult,
);
export type ToolResult = Static<typeof ToolResult>;

export type DisplayableToolResult = Exclude<ToolResult, HiddenResult>;

export const isDisplayableToolResult = (
  result: ToolResult,
): result is DisplayableToolResult => result.type !== "hidden";

const OpenAIReasoningReplayItem = RRecord({
  id: String,
  encryptedContent: String,
});

// NOTE: In January 2026, we decided to migrate the agent framework to use static cell IDs,
// instead of cell IDs. The entity ref system always stored staticCellIds, but e.g. tool finished
// and what would be provided to the frontend would be cellIds. This caused problems for the
// chat with app agent - because the cell IDs were tied to the latest published hex version,
// inline cell links or cell-based tool results in old chats would break when the app was republished.
//
// There are still historical threads that use cellId, and those will need to be handled by the
// frontend in perpetuity. Going forward, all tool results will contain staticCellIds, not cellIds

export const MessageToolResultBlock = RRecord({
  type: Literal("tool_result"),
  toolName: String,
  result: ToolResult,
  // Arguments are optional for backwards compatibility with old tool results. We can make this required after > 90 days.
  arguments: Optional(String.Or(Null)),
  staticCellId: Optional(StaticCellId.Or(Null)),
  providerMetadata: Optional(
    RRecord({
      toolCallId: Optional(String),
      provider: Optional(LlmProviderLiteral),
      phase: Optional(AssistantPhaseLiteral),
      openaiReasoningEncryptedContents: Optional(
        RArray(OpenAIReasoningReplayItem),
      ),
      openaiFunctionCallNamespace: Optional(String),
    }),
  ),
  /** @deprecated Use staticCellId instead. */
  cellId: Optional(CellId.Or(Null)),
});
export type MessageToolResultBlock = Static<typeof MessageToolResultBlock>;

export const CellMentionBlock = RRecord({
  type: Literal("cell_mention"),
  // TODO(ACORE-116): add staticCellId in a follow up PR
  cellId: CellId,
});
export type CellMentionBlock = Static<typeof CellMentionBlock>;

/**
 * Cell mention that points at a cell in a *different* hex than the thread's
 * working hex — specifically, a cell from the published app the user was
 * viewing when they sent the message (ask-from-app). Persisted separately
 * from `cell_mention` so renderers can resolve the label/cellId through the
 * viewing-app's Redux slice (rather than the chat's working hex), and so the
 * server can render it with a project-scoped ref.
 */
export const ViewingAppCellMentionBlock = RRecord({
  type: Literal("viewing_app_cell_mention"),
  cellId: CellId,
});
export type ViewingAppCellMentionBlock = Static<
  typeof ViewingAppCellMentionBlock
>;

export const MAX_APP_ELEMENT_DATA_FILE_NAME_CHARS = 128;
const AppElementDataFileName = String.withConstraint(
  (dataFileName) =>
    (dataFileName.length > 0 &&
      dataFileName.length <= MAX_APP_ELEMENT_DATA_FILE_NAME_CHARS) ||
    `dataFileName must be between 1 and ${MAX_APP_ELEMENT_DATA_FILE_NAME_CHARS} characters`,
);

/**
 * A chart element the user selected in a generative data app's edit mode
 * (component + region, optionally narrowed by instanceId/seriesKey). Serialized
 * subset of the iframe ChartElementSelection — geometry and render-closure
 * context are dropped since the agent only needs identity + current values.
 *
 * `kind` is optional here and nowhere else: rows persisted before text
 * selections existed carry no discriminator, and a chart is what they are.
 */
export const SerializedChartElementSelection = RRecord({
  kind: Optional(Literal("chart")),
  component: String,
  label: Optional(String),
  instanceId: Optional(String),
  region: String,
  seriesKey: Optional(String),
  currentValues: Optional(Dictionary(Unknown, String)),
  dataFileName: Optional(AppElementDataFileName),
  dataPreview: Optional(String),
  dataPreviewTotalChars: Optional(RNumber),
});
export type SerializedChartElementSelection = Static<
  typeof SerializedChartElementSelection
>;

/**
 * A text element the user selected in a generative app. `elementType` is the
 * tag ("H1") and `label` the snippet — together they say which element this
 * was, which is all the agent needs.
 */
export const SerializedTextElementSelection = RRecord({
  kind: Literal("text"),
  elementType: String,
  label: Optional(String),
});
export type SerializedTextElementSelection = Static<
  typeof SerializedTextElementSelection
>;

/**
 * Text arm first: a legacy chart row has no `kind` and fails it immediately,
 * while a text row lacks the `component`/`region` the chart arm requires.
 */
export const SerializedElementSelection = Union(
  SerializedTextElementSelection,
  SerializedChartElementSelection,
);
export type SerializedElementSelection = Static<
  typeof SerializedElementSelection
>;

export const MessageAppElementMentionBlock = RRecord({
  type: Literal("app_element_mention"),
  element: SerializedElementSelection,
});
export type MessageAppElementMentionBlock = Static<
  typeof MessageAppElementMentionBlock
>;

export const MessageTableMentionBlock = RRecord({
  type: Literal("table_mention"),
  tableId: DataSourceTableId,
  tableName: Optional(String),
});
export type MessageTableMentionBlock = Static<typeof MessageTableMentionBlock>;

export const MessageDataframeMentionBlock = RRecord({
  type: Literal("dataframe_mention"),
  dataframeName: MentionedDataframeName,
});
export type MessageDataframeMentionBlock = Static<
  typeof MessageDataframeMentionBlock
>;

export const MessageSemanticDatasetMentionBlock = RRecord({
  type: Literal("semantic_dataset_mention"),
  semanticDatasetId: SemanticDatasetId,
  semanticDatasetName: Optional(String),
  semanticDatasetTitle: Optional(String),
  semanticProjectId: Optional(SemanticProjectId),
});
export type MessageSemanticDatasetMentionBlock = Static<
  typeof MessageSemanticDatasetMentionBlock
>;

export const MessageSemanticViewMentionBlock = RRecord({
  type: Literal("semantic_view_mention"),
  semanticViewId: SemanticViewId,
  semanticViewName: Optional(String),
  semanticViewTitle: Optional(String),
  semanticProjectId: Optional(SemanticProjectId),
});
export type MessageSemanticViewMentionBlock = Static<
  typeof MessageSemanticViewMentionBlock
>;

export const MessageProjectMentionBlock = RRecord({
  type: Literal("project_mention"),
  // The mention block is agent-facing so using projectId instead of hexId is less confusing to the agent.
  projectId: HexId,
  projectName: Optional(String),
});
export type MessageProjectMentionBlock = Static<
  typeof MessageProjectMentionBlock
>;

export const MessageDataConnectionMentionBlock = RRecord({
  type: Literal("data_connection_mention"),
  dataConnectionId: DataConnectionId,
});
export type MessageDataConnectionMentionBlock = Static<
  typeof MessageDataConnectionMentionBlock
>;

/**
 * File attached directly to the prompt (used by ask agent, threads).
 * Contains only the filename since these are uploads to be performed on prompt submission.
 */
export const MessageFileAttachmentBlock = RRecord({
  type: Literal("file_attachment"),
  fileName: String,
});
export type MessageFileAttachmentBlock = Static<
  typeof MessageFileAttachmentBlock
>;

/**
 * File mentioned via @ context selector (used by notebook agent).
 * References a project file by ID, allowing content retrieval via GET_FILE_CONTENTS tool.
 */
export const MessageFileMentionBlock = RRecord({
  type: Literal("file_mention"),
  fileId: FileId,
});
export type MessageFileMentionBlock = Static<typeof MessageFileMentionBlock>;

/**
 * @deprecated - It is assumed that the user accepted the edit so we only need to store rejected edits.
 *
 * A block that represents an acceptance of agent edits.
 */
export const MessageAcceptBlock = RRecord({
  type: Literal("acceptCellEdit"),
  staticCellId: Optional(StaticCellId.Or(Null)),
  /** @deprecated Use staticCellId instead. */
  cellId: Optional(CellId.Or(Null)),
});
export type MessageAcceptBlock = Static<typeof MessageAcceptBlock>;

/**
 * A block that represents a rejection of agent edits.
 */
export const MessageRejectBlock = RRecord({
  type: Literal("rejectCellEdit"),
  staticCellId: Optional(StaticCellId.Or(Null)),
  /** @deprecated Use staticCellId instead. */
  cellId: Optional(CellId.Or(Null)),
});
export type MessageRejectBlock = Static<typeof MessageRejectBlock>;

/**
 * A block that represents a rejection of semantic project file edits.
 */
export const MessageRejectSemanticEditBlock = RRecord({
  type: Literal("rejectSemanticEdit"),
  semanticProjectFileId: Optional(SemanticProjectFileId.Or(Null)),
  filePath: Optional(SemanticProjectFilePath.Or(Null)),
});
export type MessageRejectSemanticEditBlock = Static<
  typeof MessageRejectSemanticEditBlock
>;

export const ErrorBlock = RRecord({
  type: Literal("error"),
  message: String,
  errorType: Union(
    Literal("cancelled"),
    Literal("fallback"),
    Literal("handled"),
  ),
});
export type ErrorBlock = Static<typeof ErrorBlock>;

// Special block type used to separate thinking blocks when in some contexts
// such as MCP.
// Handled by default case in rendering (returns null, invisible)
export const BlockSeparatorBlock = RRecord({
  type: Literal("block_separator"),
});
export type BlockSeparatorBlock = Static<typeof BlockSeparatorBlock>;

export const MessageContentBlock = Union(
  MessageThinkingBlock,
  MessageTextBlock,
  MessageCompressionTriggeredBlock,
  MessageServerCompactionBlock,
  MessageToolCallBlock,
  MessageToolResultBlock,
  ErrorBlock,
  CellMentionBlock,
  ViewingAppCellMentionBlock,
  MessageAppElementMentionBlock,
  MessageTableMentionBlock,
  MessageDataframeMentionBlock,
  MessageSemanticDatasetMentionBlock,
  MessageSemanticViewMentionBlock,
  MessageProjectMentionBlock,
  MessageDataConnectionMentionBlock,
  MessageAcceptBlock,
  MessageRejectBlock,
  MessageRejectSemanticEditBlock,
  MessageFileAttachmentBlock,
  MessageFileMentionBlock,
  MessageAskUserBlock,
  BlockSeparatorBlock,
);
export type MessageContentBlock = Static<typeof MessageContentBlock>;

/**
 * A simpler representation of a chat thread message for evals
 */
export const ConversationMessage = RRecord({
  role: ConversationMessageRoleLiteral,
  content: RArray(MessageContentBlock),
  agentInstruction: Optional(String.Or(Null)),
});
export type ConversationMessage = Static<typeof ConversationMessage>;

// The source of the agent chat thread
export const AgentChatMessageSourceLiteral = Union(
  Literal("HEX"),
  Literal("SLACK"),
  Literal("MCP"),
  Literal("TASK"),
  Literal("PUBLIC_API"),
  Literal("CUSTOMER_EVAL"),
);
export type AgentChatMessageSource = Static<
  typeof AgentChatMessageSourceLiteral
>;
export const AgentChatMessageSource = getNormalEnum(
  AgentChatMessageSourceLiteral,
);

/**
 * Debug information attached to agent chat messages for troubleshooting.
 * This metadata can be copied and sent to support for debugging agent issues.
 */
export interface AgentChatMessageDebugInfo {
  agentChatThreadId: AgentChatThreadId;
  hexId?: HexId | null;
  semanticProjectId?: SemanticProjectId | null;
  agentChatThreadType: AgentChatThreadType;
  traceId: string;
  /** Only present if running in Temporal workflow */
  workflowId?: string;
}
